import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { X, List } from 'lucide-react';
import './Calendar.css';

export default function Calendar({ activities, onEventClick, onClose }) {
  // Debug: Log what Calendar receives
  console.log('Calendar PROPS received:', { activitiesCount: activities?.length, activitiesType: typeof activities, isArray: Array.isArray(activities) });

  // Helper function - defined first
  function getPriorityColor(priority) {
    switch (priority) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      default:
        return '#3b82f6';
    }
  }

  // Helper function for event clicks - defined early
  function handleEventClick(info) {
    if (onEventClick) {
      onEventClick(info.event.extendedProps.activity);
    }
  }

  // Ensure activities is always an array to prevent crashes
  const safeActivities = Array.isArray(activities) ? activities : [];
  
  // Show ALL activities - those without dueDate will default to today
  const datedActivities = safeActivities.map(a => ({
    ...a,
    dueDate: a.dueDate || new Date().toISOString().split('T')[0] // Default to today if no due date
  }));

  // Helper: Parse date string YYYY-MM-DD to Date object (treat as local time)
  function parseLocalDate(dateStr) {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // Month is 0-indexed
  }

  // Helper: Format Date to YYYY-MM-DD string
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper: Generate recurring event dates
  function generateRecurringEvents(activity) {
    const events = [];
    const isInactive = activity.active === false;
    const isCompleted = activity.status === 'completed';
    const baseColor = isInactive
      ? '#6b7280'
      : (isCompleted ? '#9ca3af' : getPriorityColor(activity.priority));
    const cls = [];
    if (isCompleted) cls.push('fc-event-completed');
    if (isInactive) cls.push('fc-event-inactive');

    // Parse due date correctly as local date
    const dueDate = parseLocalDate(activity.dueDate);
    if (!dueDate) {
      return []; // No valid due date - shouldn't happen with our default above
    }

    // If not recurring, just return the single event
    if (!activity.isRecurring) {
      return [{
        id: activity.id,
        title: activity.title,
        start: activity.dueDate,
        allDay: true,
        extendedProps: { activity, isRecurringInstance: false },
        backgroundColor: baseColor,
        borderColor: baseColor,
        classNames: cls
      }];
    }

    // Ensure recurrenceDays is an array
    const recurrenceDays = Array.isArray(activity.recurrenceDays) ? activity.recurrenceDays : [];

    // For weekly recurrence with specific days
    if (activity.recurrenceType === 'weekly' && recurrenceDays.length > 0) {
      const startDate = new Date(dueDate);
      const endDate = activity.recurrenceEndDate 
        ? parseLocalDate(activity.recurrenceEndDate)
        : new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000); // Show 3 months ahead

      if (!endDate) {
        return []; // Invalid end date
      }

      // Generate events for each selected day of the week
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
        
        if (recurrenceDays.includes(dayOfWeek)) {
          // Only show if on or after the start date
          if (currentDate >= startDate) {
            const eventDate = formatDate(currentDate);
            const isFirstOccurrence = eventDate === activity.dueDate;
            
            events.push({
              id: `${activity.id}-${eventDate}`,
              title: activity.title,
              start: eventDate,
              allDay: true,
              extendedProps: { 
                activity, 
                isRecurringInstance: true,
                instanceDate: eventDate,
                isFirstOccurrence
              },
              backgroundColor: isFirstOccurrence ? baseColor : adjustColorOpacity(baseColor, 0.7),
              borderColor: baseColor,
              classNames: cls
            });
          }
        }
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
        
        // Limit to prevent too many events
        if (events.length >= 52) break; // Max 52 instances (1 year weekly)
      }
      
      return events;
    }

    // For other recurrence types (daily, monthly, yearly) - show next occurrence
    const nextDate = new Date(dueDate);
    const endDate = activity.recurrenceEndDate 
      ? parseLocalDate(activity.recurrenceEndDate)
      : new Date(nextDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    
    if (!endDate) {
      return []; // Invalid end date
    }
    
    const interval = activity.recurrenceInterval || 1;
    let count = 0;
    const maxEvents = 12; // Limit instances
    
    while (nextDate <= endDate && count < maxEvents) {
      const eventDate = formatDate(nextDate);
      const isFirstOccurrence = count === 0;
      
      events.push({
        id: `${activity.id}-${eventDate}`,
        title: activity.title,
        start: eventDate,
        allDay: true,
        extendedProps: { 
          activity, 
          isRecurringInstance: true,
          instanceDate: eventDate,
          isFirstOccurrence
        },
        backgroundColor: isFirstOccurrence ? baseColor : adjustColorOpacity(baseColor, 0.7),
        borderColor: baseColor,
        classNames: cls
      });
      
      // Calculate next date based on recurrence type
      switch (activity.recurrenceType) {
        case 'daily':
          nextDate.setDate(nextDate.getDate() + interval);
          break;
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + (7 * interval));
          break;
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + interval);
          break;
        case 'yearly':
          nextDate.setFullYear(nextDate.getFullYear() + interval);
          break;
        default:
          nextDate.setDate(nextDate.getDate() + 7);
      }
      count++;
    }
    
    return events;
  }

  // Helper: Adjust color opacity for recurring instances
  function adjustColorOpacity(hexColor, opacity) {
    // Handle invalid colors
    if (!hexColor || typeof hexColor !== 'string') {
      return `rgba(59, 130, 246, ${opacity})`; // Default blue
    }
    
    // Normalize hex color
    let hex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
    
    // Handle shorthand (e.g., "fff" -> "ffffff")
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    
    // Validate hex length
    if (hex.length !== 6) {
      return `rgba(59, 130, 246, ${opacity})`; // Default blue
    }
    
    // Convert hex to rgba
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Create calendar events by expanding recurring chores
  const events = datedActivities.flatMap(activity => generateRecurringEvents(activity));

  // Open calendar at today's date
  const today = new Date().toISOString().split('T')[0];
  const initialDate = today;

  return (
    <div className="calendar-container">
      {/* Mobile Header with Close Button */}
      <div className="calendar-mobile-header">
        <h3 className="calendar-title">Calendar View</h3>
        <button
          className="calendar-close-btn"
          onClick={onClose}
          title="Back to list"
        >
          <List size={20} />
          <span>Back to List</span>
        </button>
      </div>

      <FullCalendar
        key={initialDate}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        initialDate={initialDate}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek'
        }}
        navLinks={true}
        height="auto"
        nowIndicator={true}
        dayMaxEvents={true}
        events={events}
        eventClick={handleEventClick}
        dayCellContent={(arg) => {
          return arg.dayNumberText;
        }}
        eventDidMount={(arg) => {
          // Add tooltip with notes
          if (arg.event.extendedProps.notes) {
            arg.el.title = arg.event.extendedProps.notes;
          }
        }}
      />
    </div>
  );
}
